export const SERVER_AMOUNT_GATE_STATUS = Object.freeze({
  LEGACY_RESPONSE: 'LEGACY_RESPONSE',
  MATCH: 'MATCH',
  MISMATCH: 'MISMATCH',
  INVALID_CURRENT_AMOUNT: 'INVALID_CURRENT_AMOUNT',
});

const roundEurosFromCents = (amountCents) => Math.round((amountCents / 100) * 100) / 100;

const normalizeIntegerCents = (value, { allowZero = false } = {}) => {
  if (value === null || value === undefined || value === '') return null;
  const cents = Math.round(Number(value));
  if (!Number.isFinite(cents)) return null;
  if (allowZero ? cents < 0 : cents <= 0) return null;
  return cents;
};

const normalizeEuroAmountToCents = (value, { allowZero = false } = {}) => {
  if (value === null || value === undefined || value === '') return null;
  const euros = Number(value);
  if (!Number.isFinite(euros)) return null;
  if (allowZero ? euros < 0 : euros <= 0) return null;
  return Math.round(euros * 100);
};

const resolveCents = ({ cents, euros, allowZero = false }) => (
  normalizeIntegerCents(cents, { allowZero })
  ?? normalizeEuroAmountToCents(euros, { allowZero })
);

export const getReservationServerPricing = (reservation) => {
  const totalAmountCents = resolveCents({
    cents: reservation?.totalAmountCents,
    euros: reservation?.totalPrice,
  });

  if (!totalAmountCents) {
    return null;
  }

  const originalAmountCents = resolveCents({
    cents: reservation?.originalAmountCents,
    euros: reservation?.originalTotalPrice,
  }) ?? totalAmountCents;

  const derivedDiscountAmountCents = Math.max(originalAmountCents - totalAmountCents, 0);
  const explicitDiscountAmountCents = resolveCents({
    cents: reservation?.campaignDiscountAmountCents,
    euros: reservation?.campaignDiscountAmount,
    allowZero: true,
  });
  const campaignDiscountAmountCents =
    explicitDiscountAmountCents !== null && explicitDiscountAmountCents <= originalAmountCents
      ? explicitDiscountAmountCents
      : derivedDiscountAmountCents;

  return {
    totalAmountCents,
    originalAmountCents,
    campaignDiscountAmountCents,
    totalPrice: roundEurosFromCents(totalAmountCents),
    originalTotalPrice: roundEurosFromCents(originalAmountCents),
    campaignDiscountAmount: roundEurosFromCents(campaignDiscountAmountCents),
    campaignApplied: campaignDiscountAmountCents > 0,
  };
};

export const buildServerPricingUpdateEntries = (reservation) => {
  const pricing = getReservationServerPricing(reservation);
  if (!pricing) return [];

  return [
    ['totalPrice', pricing.totalPrice],
    ['serverTotalAmountCents', pricing.totalAmountCents],
    ['originalAmountCents', pricing.originalAmountCents],
    ['campaignDiscountAmountCents', pricing.campaignDiscountAmountCents],
    ['campaignApplied', pricing.campaignApplied],
    ['serverPriceSynced', true],
  ];
};

export const getReservationAmountGate = ({ reservation, currentAmountCents }) => {
  const serverPricing = getReservationServerPricing(reservation);

  if (!serverPricing) {
    return {
      status: SERVER_AMOUNT_GATE_STATUS.LEGACY_RESPONSE,
      hasServerAmount: false,
      matches: true,
      needsUserReconfirmation: false,
      serverPricing: null,
      updateEntries: [],
    };
  }

  const normalizedCurrentAmountCents = normalizeIntegerCents(currentAmountCents);
  if (!normalizedCurrentAmountCents) {
    return {
      status: SERVER_AMOUNT_GATE_STATUS.INVALID_CURRENT_AMOUNT,
      hasServerAmount: true,
      matches: false,
      needsUserReconfirmation: true,
      serverPricing,
      updateEntries: buildServerPricingUpdateEntries(reservation),
    };
  }

  const matches = normalizedCurrentAmountCents === serverPricing.totalAmountCents;

  return {
    status: matches
      ? SERVER_AMOUNT_GATE_STATUS.MATCH
      : SERVER_AMOUNT_GATE_STATUS.MISMATCH,
    hasServerAmount: true,
    matches,
    needsUserReconfirmation: !matches,
    serverPricing,
    updateEntries: matches ? [] : buildServerPricingUpdateEntries(reservation),
  };
};
